import { GetTokenSilentlyOptions, LogoutOptions } from '@auth0/auth0-react';
import { store } from '../redux/store';
import { isTokenExpired } from '../utils/isTokenExpired';
import refreshTokenOrLogout from '../utils/refreshTokenOrLogout';
import { PostHog } from 'posthog-js';
import { addLearnosityBffBreadcrumb, captureLearnosityException } from '../utils/learnositySentry';

export interface DiagnosticAbilityEstimate {
  status: string | null;
  abilityEstimate: number | null;
  standardError: number | null;
}

/**
 * Fetches the current adaptive ability estimate (theta) for an in-progress diagnostic session from
 * the lean BFF endpoint. Used during the diagnostic to decide whether to offer the "I'm Done"
 * early-end option. Returns `null` on error / when unavailable, so the diagnostic keeps running.
 */
export const getDiagnosticAbilityEstimate = async (
  sessionId: string,
  getAccessTokenSilently: (options: GetTokenSilentlyOptions) => Promise<string>,
  logout: (options?: LogoutOptions) => Promise<void>,
  posthog: PostHog,
  learnerId?: string,
): Promise<DiagnosticAbilityEstimate | null> => {
  let accessToken = store.getState().accessToken.value;

  if (isTokenExpired()) {
    accessToken = (await refreshTokenOrLogout(getAccessTokenSilently, logout, posthog)) || null;
  }

  const fields = { assessmentType: 'diagnostic', learnerId, sessionId };
  addLearnosityBffBreadcrumb('diagnostic_ability_estimate', fields);

  try {
    const response = await fetch(
      `${import.meta.env.VITE_BFF_URL}/v1/learnosity/diagnostics/ability-estimate?sessionId=${sessionId}`,
      {
        headers: {
          'Content-type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    if (!response.ok) {
      captureLearnosityException(undefined, {
        operation: 'diagnostic_ability_estimate',
        ...fields,
        status: response.status,
        statusText: response.statusText,
      });
      return null;
    }

    const data = await response.json();

    const result: DiagnosticAbilityEstimate = {
      status: typeof data?.status === 'string' ? data.status : null,
      abilityEstimate: typeof data?.abilityEstimate === 'number' ? data.abilityEstimate : null,
      standardError: typeof data?.standardError === 'number' ? data.standardError : null,
    };
    const abilityEstimate =
      typeof data?.abilityEstimate === 'number' && Number.isFinite(data.abilityEstimate) ? data.abilityEstimate : null;
    const standardError =
      typeof data?.standardError === 'number' && Number.isFinite(data.standardError) && data.standardError >= 0
        ? data.standardError
        : null;

    addLearnosityBffBreadcrumb('diagnostic_ability_estimate', {
      ...fields,
      status: response.status,
      responseStatus: result.status ?? undefined,
      abilityEstimate,
      standardError,
    });

    return result;
  } catch (error) {
    captureLearnosityException(error, { operation: 'diagnostic_ability_estimate', ...fields });
    return null;
  }
};
